import { demoCompany, demoDepartments, demoShifts, demoTimetableEvents, demoUsers } from "./demoData";
import { isDemoMode } from "./demoMode";

const READ_METHODS = new Set(["get", "head", "options"]);

const userListStrings = demoUsers.map((user, index) =>
  [
    user.UserID,
    (index % demoDepartments.length) + 1,
    user.first_name,
    user.last_name,
    user.email,
    "1",
    user.employment_level === 1 ? 40 : 24,
    index % 3 === 0 ? "0" : "1",
  ].join(", ")
);

const departmentNames = demoDepartments.map((department) => department.name);

function dateKey(date) {
  return date.toISOString().slice(0, 10);
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function startOfWeek(date = new Date()) {
  const monday = new Date(date);
  const weekday = monday.getDay();
  monday.setHours(12, 0, 0, 0);
  monday.setDate(monday.getDate() - (weekday === 0 ? 6 : weekday - 1));
  return monday;
}

function isoWeekNumber(date = new Date()) {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil((((target - yearStart) / 86400000) + 1) / 7);
}

function dateAtOffset(offset) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return dateKey(date);
}

function timeInMinutes(value) {
  const [hours, minutes] = String(value || "00:00").split(":").map(Number);
  return (hours * 60) + minutes;
}

function isActiveAtTime(event, date = new Date()) {
  const now = (date.getHours() * 60) + date.getMinutes();
  const start = timeInMinutes(event.start_time);
  const end = timeInMinutes(event.end_time);
  return end > start
    ? now >= start && now < end
    : now >= start || now < end;
}

function demoPlanningStatus(dates) {
  let openShiftCount = 0;
  const departments = demoDepartments.map((department, departmentIndex) => ({
    department_id: department.DepartmentID,
    department: department.name,
    shorthand: department.shorthand_symbol,
    color: department.color_hex,
    days: dates.map((date, dayIndex) => {
      const required = 2 + ((departmentIndex + dayIndex) % 3);
      const missing = (departmentIndex + dayIndex) % 4 === 1 ? 1 : 0;
      const covered = required - missing;
      openShiftCount += missing;
      const shift = demoShifts[(departmentIndex + dayIndex) % demoShifts.length];
      return {
        date,
        has_demand: true,
        required,
        covered,
        planned: covered,
        missing,
        open_shifts: missing
          ? [{
              shift_id: shift.ShiftID,
              shorthand: `${department.shorthand_symbol}-${shift.shorthand_symbol}`,
              shift_name: shift.name,
              missing,
              hours: 8,
            }]
          : [],
      };
    }),
  }));

  return {
    start_date: dates[0],
    end_date: dates[dates.length - 1],
    dates,
    open_shift_count: openShiftCount,
    departments,
  };
}

function dashboardResponse(config) {
  const today = dateAtOffset(0);
  const tomorrow = dateAtOffset(1);
  const currentMonth = monthKey(new Date());
  const requestedMonth = requestParam(config, "month", currentMonth);
  const monthMatch = /^(\d{4})-(\d{2})$/.exec(requestedMonth);
  const requestedYear = Number(monthMatch?.[1]);
  const requestedMonthNumber = Number(monthMatch?.[2]);
  const hasValidMonth = Number.isInteger(requestedYear)
    && requestedYear >= 2
    && requestedYear <= 9998
    && requestedMonthNumber >= 1
    && requestedMonthNumber <= 12;
  const selectedYear = hasValidMonth ? requestedYear : new Date().getFullYear();
  const selectedMonthIndex = hasValidMonth ? requestedMonthNumber - 1 : new Date().getMonth();
  const dashboardEvents = demoTimetableEvents.slice(0, 5);
  const currentShifts = dashboardEvents
    .map((event, index) => ({ event, index }))
    .filter(({ event }) => isActiveAtTime(event))
    .map(({ event, index }) => {
      const endDate = timeInMinutes(event.end_time) <= timeInMinutes(event.start_time)
        ? tomorrow
        : today;
      return {
        id: event.TimetableID,
        name: event.user_name,
        department_id: event.DepartmentID,
        department: event.department_name,
        department_color: event.color_hex,
        shift_name: index === 4 ? "Eventdienst" : event.shift_name,
        shift_type: index === 4 ? "custom_shift" : "shift",
        date: today,
        start_at: `${today}T${event.start_time}`,
        end_at: `${endDate}T${event.end_time}`,
        start: event.start_time,
        end: event.end_time,
      };
    });
  const upcomingShifts = demoTimetableEvents.slice(5, 12).map((event, index) => {
    const date = dateAtOffset(index < 4 ? 1 : 2);
    return {
      id: event.TimetableID,
      name: event.user_name,
      department_id: event.DepartmentID,
      department: event.department_name,
      department_color: event.color_hex,
      shift_name: index === 1 ? "Eigener Eventdienst" : event.shift_name,
      shift_type: index === 1 ? "custom_shift" : "shift",
      date,
      start_at: `${date}T${event.start_time}`,
      start: event.start_time,
      end: event.end_time,
    };
  });
  const monthlyByDepartment = demoDepartments.map((department, index) => ({
    department: department.name,
    hours: [184, 156, 128][index],
    percent: [39, 33, 28][index],
  }));
  const monthlyTotal = monthlyByDepartment.reduce((sum, row) => sum + row.hours, 0);
  const daysInMonth = new Date(selectedYear, selectedMonthIndex + 1, 0, 12).getDate();
  const rawDailyHours = Array.from({ length: daysInMonth }, (_, index) => (
    [18, 22, 24, 20, 28, 12, 0][index % 7]
  ));
  const rawDailyTotal = rawDailyHours.reduce((sum, hours) => sum + hours, 0);
  const monthlyHoursByDay = rawDailyHours.map((hours, index) => ({
    date: dateKey(new Date(selectedYear, selectedMonthIndex, index + 1, 12)),
    hours: Math.round((hours / rawDailyTotal) * monthlyTotal * 100) / 100,
  }));
  const roundedDailyTotal = monthlyHoursByDay.reduce((sum, row) => sum + row.hours, 0);
  const lastWorkedDay = [...monthlyHoursByDay].reverse().find((row) => row.hours > 0);
  if (lastWorkedDay) {
    lastWorkedDay.hours = Math.round((lastWorkedDay.hours + monthlyTotal - roundedDailyTotal) * 100) / 100;
  }

  return {
    current_shifts: currentShifts,
    upcoming_shifts: upcomingShifts,
    full_time_count: demoUsers.filter((user) => user.employment_level === 1).length,
    part_time_count: demoUsers.filter((user) => user.employment_level !== 1).length,
    worker_count: demoUsers.length,
    department_color_map: Object.fromEntries(
      demoDepartments.map((department) => [department.name, department.color_hex])
    ),
    monthly_hours_by_day: monthlyHoursByDay,
    monthly_hours_by_department: monthlyByDepartment,
    monthly_total_hours: monthlyTotal,
    monthly_comparison: {
      previous_month: monthKey(new Date(selectedYear, selectedMonthIndex - 1, 1, 12)),
      previous_total_hours: 438,
      change_percent: Math.round(((monthlyTotal - 438) / 438) * 1000) / 10,
    },
    planning_status: demoPlanningStatus(
      Array.from({ length: 7 }, (_, index) => dateAtOffset(index))
    ),
    access_level: "Super_Admin",
    location_name: "Demo Standort",
    features: {},
  };
}

function usersResponse() {
  return {
    users: demoUsers.map((user, index) => {
      const department = demoDepartments[index % demoDepartments.length];
      return {
        id: user.UserID,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        employment: user.employment,
        employment_level: user.employment_level,
        selected_departments: [department.name],
        selected_department_ids: [department.DepartmentID],
        experience: "Demo",
        in_training: index % 5 === 0,
        access_level: index === 0 ? "Admin" : "User",
        access_level_label: index === 0 ? "Admin" : "User",
        preferred_ee: "",
        avoidable_ee: "",
        regular_user: true,
        hourly_wage: "",
        attachment_count: 0,
      };
    }),
    departments: departmentNames,
    department_options: demoDepartments.map((department) => ({
      id: department.DepartmentID,
      name: department.name,
      shorthand_symbol: department.shorthand_symbol,
    })),
    access_level: "Super_Admin",
  };
}

function currentUserResponse() {
  return {
    first_name: "Demo",
    last_name: "Admin",
    email: "demo@timetab.ch",
    company_name: demoCompany.name,
    access_level: "Super_Admin",
    availability_view: true,
    avatar_url: "",
  };
}

function requestParam(config, name, fallback = "") {
  if (config.params?.[name] != null) return String(config.params[name]);
  const url = new URL(config.url, window.location.origin);
  return url.searchParams.get(name) || fallback;
}

function localDateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function dateRange(start, end, maximumDays = 367) {
  const days = [];
  const cursor = new Date(start);
  cursor.setHours(12, 0, 0, 0);
  const finalDay = new Date(end);
  finalDay.setHours(12, 0, 0, 0);
  while (cursor <= finalDay && days.length < maximumDays) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function requestedRange(config, fallbackToMonth = false) {
  const startValue = requestParam(config, "start_date");
  const endValue = requestParam(config, "end_date");
  if (startValue && endValue) {
    return {
      start: new Date(`${startValue}T12:00:00`),
      end: new Date(`${endValue}T12:00:00`),
    };
  }

  if (fallbackToMonth) {
    const monthValue = requestParam(config, "month", localDateKey(new Date()).slice(0, 7));
    const [year, month] = monthValue.split("-").map(Number);
    return {
      start: new Date(year, month - 1, 1, 12),
      end: new Date(year, month, 0, 12),
    };
  }

  const start = startOfWeek();
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

function demoAvailabilitySettings() {
  return {
    can_edit_settings: true,
    location_id: 1,
    location_name: "Demo Standort",
    effective: { availability_mode: "absence", approval_required: true },
    user: { availability_mode: "absence", approval_required: false, has_user_setting: true },
    company: {
      availability_mode: "absence",
      approval_required: true,
      edit_lock_enabled: false,
      edit_lock_day_of_month: "",
      edit_lock_horizon_months: "",
    },
    freeze_periods: [],
    holiday_types: [
      { id: 1, name: "Ferien", hours_per_day: 8, start_time: "00:00", end_time: "23:59", is_active: true, is_system_default: true },
      { id: 2, name: "Weiterbildung", hours_per_day: 8, start_time: "00:00", end_time: "23:59", is_active: true, is_system_default: false },
    ],
  };
}

function demoAvailabilityRows(start, end) {
  const days = dateRange(start, end);
  return demoUsers.map((user, userIndex) => {
    const perDay = {};
    days.forEach((date, dayIndex) => {
      const weekday = (date.getDay() + 6) % 7;
      const isWeekend = weekday > 4;
      const isHoliday = userIndex % 5 === 0 && [2, 3].includes(dayIndex % 14);
      const isExplicitAbsence = userIndex % 5 === 1 && dayIndex % 8 === 5;
      const isPartialAvailability = !isWeekend && (userIndex + dayIndex) % 4 === 0;
      const isPending = isExplicitAbsence && (userIndex + dayIndex) % 3 === 0;
      let status = isWeekend ? "absence" : "availability";
      let availabilityTime = isWeekend ? "Abwesend" : "Verfügbar";
      let isFullDay = true;
      let isDefault = !isHoliday && !isExplicitAbsence && !isPartialAvailability;

      if (isHoliday) {
        status = "holiday";
        availabilityTime = "Ferien";
        isDefault = false;
      } else if (isExplicitAbsence) {
        status = "absence";
        availabilityTime = "Abwesend";
        isDefault = false;
      } else if (isPartialAvailability) {
        status = "availability";
        availabilityTime = userIndex % 2 === 0 ? "08:00 - 16:00" : "12:00 - 20:00";
        isFullDay = false;
        isDefault = false;
      }

      perDay[localDateKey(date)] = {
        availability_time: availabilityTime,
        status,
        is_full_day: isFullDay,
        is_default: isDefault,
        is_pending: isPending,
        has_comment: isExplicitAbsence || isPartialAvailability,
        isHoliday,
        holiday: isHoliday,
        holiday_name: isHoliday ? "Ferien" : "",
      };
    });

    return {
      user_id: user.UserID,
      name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      regular_user: true,
      availability_mode: "absence",
      default_availability: [0, 1, 2, 3, 4].map((weekday) => ({
        weekday,
        start_time: "08:00",
        end_time: "17:00",
        is_full_day: true,
      })),
      default_weekdays: [0, 1, 2, 3, 4],
      opening_weekdays: [0, 1, 2, 3, 4, 5],
      always_available: false,
      sort_order: userIndex,
      per_day: perDay,
    };
  });
}

function availabilityOverviewResponse(config) {
  const { start, end } = requestedRange(config);
  return {
    rows: demoAvailabilityRows(start, end),
    start_date: localDateKey(start),
    end_date: localDateKey(end),
  };
}

function availabilityCalendarResponse(config) {
  const { start, end } = requestedRange(config, true);
  const requestedUserId = Number(requestParam(config, "user_id", demoUsers[0].UserID));
  const user = demoUsers.find((entry) => entry.UserID === requestedUserId) || demoUsers[0];
  const row = demoAvailabilityRows(start, end).find((entry) => entry.user_id === user.UserID);
  const days = dateRange(start, end).map((date, index) => {
    const key = localDateKey(date);
    const value = row.per_day[key];
    const entry = {
      id: `${user.UserID}-${key}`,
      kind: value.status,
      date: key,
      start_time: value.is_full_day ? "00:00" : value.availability_time.slice(0, 5),
      end_time: value.is_full_day ? "23:59" : value.availability_time.slice(-5),
      is_full_day: value.is_full_day,
      comment: value.has_comment ? "Demo-Eintrag" : "",
      release_status: value.is_pending ? "pending" : "released",
    };
    return {
      date: key,
      day: date.getDate(),
      weekday: (date.getDay() + 6) % 7,
      calendar_week: isoWeekNumber(date),
      status: value.status,
      status_source: value.is_default ? "default" : "stored",
      has_stored_entry: !value.is_default,
      is_working_day: date.getDay() > 0 && date.getDay() < 6,
      is_before_entry_date: false,
      is_editable: true,
      blocked_reason: null,
      blocked_label: null,
      blocked_message: null,
      entry_date: null,
      entries: !value.is_default && value.status !== "holiday" ? [entry] : [],
      default_entries: value.is_default ? [{ ...entry, id: `default-${index}` }] : [],
      holidays: value.status === "holiday" ? [{
        id: `holiday-${user.UserID}-${key}`,
        kind: "holiday",
        start_date: key,
        end_date: key,
        holiday_type_id: 1,
        holiday_type_name: "Ferien",
        hours_per_day: 8,
        total_hours: 8,
        approval_required: false,
      }] : [],
      has_comment: value.has_comment,
      is_pending: value.is_pending,
    };
  });
  return {
    holiday_types: demoAvailabilitySettings().holiday_types,
    selected_user_entry_date: null,
    settings: demoAvailabilitySettings(),
    month: localDateKey(start).slice(0, 7),
    month_start: localDateKey(start),
    month_end: localDateKey(end),
    selected_user_id: user.UserID,
    selected_user: `${user.first_name} ${user.last_name}`,
    selected_mode: "absence",
    working_weekdays: [0, 1, 2, 3, 4],
    days,
    summary: {
      entered: days.filter((day) => day.has_stored_entry).length,
      open: days.filter((day) => day.is_pending).length,
      holidays: days.filter((day) => day.status === "holiday").length,
      free_requests: 0,
    },
  };
}

function availabilityContextResponse() {
  const labels = demoUsers.map((user) => `${user.first_name} ${user.last_name}`);
  return {
    current_user: labels[0],
    user_list: labels,
    user_modes: Object.fromEntries(labels.map((label) => [label, "absence"])),
    selected_mode: "absence",
    selected_user_entry_date: null,
    access_level: "Super_Admin",
    can_select_users: true,
    holiday_types: demoAvailabilitySettings().holiday_types,
    settings: demoAvailabilitySettings(),
  };
}

function punchClockResponse() {
  const now = new Date();
  const checkedInAt = new Date(now.getTime() - (146 * 60 * 1000));
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const yesterdayStart = new Date(yesterday);
  yesterdayStart.setHours(8, 4, 0, 0);
  const yesterdayEnd = new Date(yesterday);
  yesterdayEnd.setHours(16, 38, 0, 0);
  return {
    punch_clock_date: localDateKey(checkedInAt),
    punch_clock_time: checkedInAt.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }),
    manual_break: 1,
    approval_required: true,
    locations: [{ id: 1, name: "Demo Standort" }],
    selected_location_id: 1,
    show_location_select: false,
    last_check_in: checkedInAt.toISOString(),
    active_entry: {
      id: 9001,
      check_in: checkedInAt.toISOString(),
      check_in_time: checkedInAt.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }),
      location_id: 1,
      location_name: "Demo Standort",
    },
    today: {
      work_minutes: 146,
      break_minutes: 0,
      target_minutes: 480,
      progress: 30,
    },
    recent_bookings: [
      { id: "9001:check_in", entry_id: 9001, date: localDateKey(checkedInAt), date_label: "Heute", time: checkedInAt.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }), type: "check_in", label: "Eingestempelt", source: "Web", approval_status: "approved" },
      { id: "9000:check_out", entry_id: 9000, date: localDateKey(yesterday), date_label: "Gestern", time: "16:38", type: "check_out", label: "Ausgestempelt", source: "Web", approval_status: "approved" },
      { id: "9000:check_in", entry_id: 9000, date: localDateKey(yesterday), date_label: "Gestern", time: "08:04", type: "check_in", label: "Eingestempelt", source: "Web", approval_status: "approved" },
    ],
    pending_entries: [
      { id: 8999, date_label: localDateKey(yesterdayStart), start: "08:15", end: "17:00", break_minutes: 30, approval_status: "pending" },
      { id: 8998, date_label: localDateKey(yesterdayEnd), start: "09:00", end: "18:10", break_minutes: 45, approval_status: "pending" },
    ],
  };
}

function approvalResponse() {
  const now = new Date();
  const buildRow = (category, index) => {
    const requester = demoUsers[(index + 1) % demoUsers.length];
    // Beim Schichttausch ist der Empfaenger die angefragte Kollegin/der
    // angefragte Kollege (backend_app/shift_exchange.py), sonst die Admin-
    // Person am Standort.
    const exchangePartner = demoUsers[(index + 2) % demoUsers.length];
    const start = new Date(now);
    start.setDate(now.getDate() + index + 1);
    start.setHours(category === "shift_exchange" ? 15 : 8, 0, 0, 0);
    const end = new Date(start);
    end.setHours(category === "shift_exchange" ? 23 : 17, 0, 0, 0);
    const sourceId = 7000 + index;
    return {
      id: `${category}:${sourceId}`,
      source: category === "punch_clock" ? "punch_clock" : "approval_request",
      source_id: sourceId,
      category,
      title: category === "shift_exchange" ? "Schichttausch" : "Stempelung",
      request_type: category,
      target_type: category,
      target_id: sourceId,
      status: "pending",
      requester_name: `${requester.first_name} ${requester.last_name}`,
      requester_email: requester.email,
      recipient_name: category === "shift_exchange"
        ? `${exchangePartner.first_name} ${exchangePartner.last_name}`
        : "Demo Admin",
      location_name: "Demo Standort",
      period: `${start.toLocaleDateString("de-CH")} ${start.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })} - ${end.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}`,
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      comment: category === "shift_exchange"
        ? `${requester.first_name} ${requester.last_name} → ${exchangePartner.first_name} ${exchangePartner.last_name}`
        : ["Vergessener Checkout", "Zeitkorrektur", "Manueller Eintrag", "Pause korrigieren", "Checkout ergänzen"][index - 3],
      break_minutes: category === "punch_clock" ? [30, 45, 30, 20, 30][index - 3] : undefined,
      created_at: new Date(now.getTime() - ((index + 1) * 60 * 60 * 1000)).toISOString(),
      can_decide: true,
    };
  };
  const rows = [
    ...Array.from({ length: 3 }, (_, index) => buildRow("shift_exchange", index)),
    ...Array.from({ length: 5 }, (_, index) => buildRow("punch_clock", index + 3)),
  ];
  return {
    rows,
    summary: { total: 8, shift_exchange: 3, punch_clock: 5, absence: 0, holiday: 0 },
    access_level: "Super_Admin",
  };
}

function demoRequiredWorkers(date, shiftIndex) {
  const weekday = date.getDay();
  if (weekday === 0) return 0;
  if (weekday === 6) return [3, 3, 2][shiftIndex] || 0;
  if (weekday === 5) return [4, 3, 3][shiftIndex] || 0;
  return [3, 2, 2][shiftIndex] || 0;
}

const DEMO_DAILY_TARGET_HOURS = 8;

/*
  Bruttominuten minus Pause, wie databases/hours_resolution.py sie rechnet.
  Die Demo liefert die Werte im Payload mit, damit sie durch denselben
  Client-Pfad laufen wie die echten Daten.
*/
function demoShiftPlannedMinutes(dayKey, startTime, endTime, breakMinutes) {
  const start = new Date(`${dayKey}T${startTime}:00`);
  const end = new Date(`${dayKey}T${endTime}:00`);
  const grossMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  return Math.max(0, grossMinutes - breakMinutes);
}

function isDemoWorkingDay(date) {
  return date.getDay() !== 0 && date.getDay() !== 6;
}

function demoMonthShiftEvents() {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const events = [];
  const usedUsersByDate = new Map();
  const workedDayKeysByUser = new Map();
  let id = 1;
  for (let day = 1; day <= lastDay; day += 1) {
    const date = new Date(now.getFullYear(), now.getMonth(), day, 12);
    const dayKey = localDateKey(date);
    const usedUserIds = new Set();
    const holidayUserIds = new Set([
      ...(day >= 10 && day <= 12 ? [1] : []),
      ...(day >= 17 && day <= 21 ? [6] : []),
      ...(day >= 24 && day <= 26 ? [11] : []),
    ]);
    const weekIndex = Math.floor((day - 1) / 7);
    for (let shiftIndex = 0; shiftIndex < demoShifts.length; shiftIndex += 1) {
      const shift = demoShifts[shiftIndex];
      const department = demoDepartments[shiftIndex % demoDepartments.length];
      const requiredWorkers = demoRequiredWorkers(date, shiftIndex);
      const departmentUsers = demoUsers.filter(
        (_user, userIndex) => userIndex % demoDepartments.length === shiftIndex
      );
      const weekdayIndex = (date.getDay() + 6) % 7;
      const pairOffset = [0, 0, 2, 2, 4, 4, 0][weekdayIndex];
      const weeklyOffset = (weekIndex + pairOffset) % departmentUsers.length;
      const availableDepartmentUsers = Array.from(
        { length: departmentUsers.length },
        (_, offset) => departmentUsers[(weeklyOffset + offset) % departmentUsers.length]
      ).filter((user) => {
        if (holidayUserIds.has(user.UserID)) return false;
        const workedDays = workedDayKeysByUser.get(user.UserID) || new Set();
        const workedPreviousFiveDays = Array.from({ length: 5 }, (_, offset) => {
          const previousDate = new Date(date);
          previousDate.setDate(date.getDate() - offset - 1);
          return localDateKey(previousDate);
        }).every((key) => workedDays.has(key));
        return !workedPreviousFiveDays;
      });
      for (let workerIndex = 0; workerIndex < requiredWorkers; workerIndex += 1) {
        const user = availableDepartmentUsers[workerIndex];
        if (!user) continue;
        usedUserIds.add(user.UserID);
        const eventId = id++;
        events.push({
          id: eventId,
          user_id: user.UserID,
          shift_id: String(shift.ShiftID),
          start_at: `${dayKey}T${shift.start_time}:00`,
          end_at: `${dayKey}T${shift.end_time}:00`,
          department_name: department.name,
          title: shift.name,
          title_short: shift.shorthand_symbol,
          department_short_name: department.shorthand_symbol,
          entry_type: "shift",
          release_status: eventId % 9 === 0 ? "draft" : "released",
          color: department.color_hex,
          breaks: [{ id: `break-${eventId}`, break_type: "duration", duration_minutes: 30 }],
          // Wie im echten Backend: geplante Minuten kommen fertig gerechnet
          // mit (databases/hours_resolution.py).
          planned_minutes: demoShiftPlannedMinutes(dayKey, shift.start_time, shift.end_time, 30),
          planned_minutes_per_day: null,
        });
      }
    }
    usedUsersByDate.set(dayKey, usedUserIds);
    usedUserIds.forEach((userId) => {
      const workedDays = workedDayKeysByUser.get(userId) || new Set();
      workedDays.add(dayKey);
      workedDayKeysByUser.set(userId, workedDays);
    });
  }
  [6, 13, 20, 27].filter((day) => day <= lastDay).forEach((day, index) => {
    const date = new Date(now.getFullYear(), now.getMonth(), day, 12);
    const dayKey = localDateKey(date);
    const usedUserIds = usedUsersByDate.get(dayKey) || new Set();
    const absenceUser = demoUsers.find((user) => !usedUserIds.has(user.UserID)) || demoUsers[demoUsers.length - 1];
    events.push({
      id: id++,
      user_id: absenceUser.UserID,
      shift_id: "",
      absence_id: index % 2 === 0 ? 1 : 2,
      start_at: `${dayKey}T00:00:00`,
      end_at: `${dayKey}T23:59:00`,
      department_name: index % 2 === 0 ? "Krankheit" : "Weiterbildung",
      title: index % 2 === 0 ? "Krankheit" : "Weiterbildung",
      title_short: index % 2 === 0 ? "KR" : "WB",
      department_short_name: "",
      entry_type: "absence",
      is_all_day: true,
      release_status: "released",
      color: index % 2 === 0 ? "#C81E4B" : "#2563EB",
      breaks: [],
      absence_hours_per_day: DEMO_DAILY_TARGET_HOURS,
      planned_minutes: null,
      planned_minutes_per_day: DEMO_DAILY_TARGET_HOURS * 60,
    });
  });
  return events;
}

function currentDemandResponse(config) {
  const requestedWeek = requestParam(config, "week_start");
  const monday = startOfWeek(requestedWeek ? new Date(`${requestedWeek}T12:00:00`) : new Date());
  const weekdayNames = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
  const dates = weekdayNames.map((weekday, weekdayIndex) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + weekdayIndex);
    return {
      date: dateKey(date),
      weekday_index: weekdayIndex,
      weekday,
      label: weekday.slice(0, 2),
      is_open: weekdayIndex !== 6,
    };
  });
  const departments = demoDepartments.map((department, index) => {
    const shift = demoShifts[index % demoShifts.length];
    return {
      id: department.DepartmentID,
      name: department.name,
      shorthand_symbol: department.shorthand_symbol,
      color_hex: department.color_hex,
      shifts: [{
        id: shift.ShiftID,
        name: shift.name,
        shorthand_symbol: shift.shorthand_symbol,
        color_hex: shift.color_hex,
        start_time: shift.start_time,
        end_time: shift.end_time,
        break_duration_minutes: 30,
      }],
    };
  });
  const demands = {};
  const overrides = {};
  const defaultDemands = {};

  departments.forEach((department, departmentIndex) => {
    department.shifts.forEach((shift) => {
      dates.forEach((day, dayIndex) => {
        const demandDate = new Date(`${day.date}T12:00:00`);
        const requiredWorkers = demoRequiredWorkers(demandDate, departmentIndex);
        const defaultValue = Math.max(requiredWorkers - (dayIndex === departmentIndex ? 1 : 0), 0);
        const key = `${department.id}:${shift.id}:${day.date}`;
        defaultDemands[`${department.id}:${shift.id}:${day.weekday_index}`] = defaultValue;
        demands[key] = requiredWorkers;
        if (demands[key] !== defaultValue) overrides[key] = demands[key];
      });
    });
  });

  return {
    locations: [{ id: 1, name: "Demo Standort" }],
    selected_location_id: 1,
    show_location_select: false,
    week_start: dateKey(monday),
    dates,
    departments,
    demands,
    overrides,
    default_demands: defaultDemands,
  };
}

function shiftplanBootstrapResponse() {
  const shiftList = demoShifts.map((shift, index) => {
    const department = demoDepartments[index % demoDepartments.length];
    return [
      String(shift.ShiftID),
      shift.name,
      `${shift.start_time} - ${shift.end_time}`,
      "",
      department.name,
      shift.shorthand_symbol,
      department.shorthand_symbol,
    ];
  });
  const colorMap = {};
  demoDepartments.forEach((department) => { colorMap[department.name] = department.color_hex; });
  demoShifts.forEach((shift) => { colorMap[String(shift.ShiftID)] = shift.color_hex; });
  const now = new Date();
  const monthKey = localDateKey(now).slice(0, 7);
  const monthLastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const userAvailabilityDefaults = demoUsers.flatMap((user) =>
    [0, 1, 2, 3, 4].map((weekday) => ({
      id: `${user.UserID}-${weekday}`,
      user_id: user.UserID,
      type: "availability",
      weekday,
      start_time: "08:00",
      end_time: "17:00",
      is_full_day: true,
      is_active: true,
      comment: "Demo-Standardtag",
    }))
  );
  const userAvailabilityEntries = demoUsers.flatMap((user, userIndex) =>
    [3, 8, 12, 17, 22, 26]
      .filter((day) => day <= monthLastDay)
      .map((day, dayIndex) => ({
        id: `${user.UserID}-${day}`,
        user_id: user.UserID,
        type: (userIndex + dayIndex) % 5 === 0 ? "absence" : "availability",
        start_at: `${monthKey}-${String(day).padStart(2, "0")}T${dayIndex % 2 === 0 ? "08:00:00" : "12:00:00"}`,
        end_at: `${monthKey}-${String(day).padStart(2, "0")}T${dayIndex % 2 === 0 ? "16:00:00" : "20:00:00"}`,
        is_full_day: (userIndex + dayIndex) % 5 === 0,
        comment: "Demo-Verfügbarkeit",
        release_status: "released",
      }))
  );

  return {
    user_list: userListStrings,
    department_list: demoDepartments.map((department) => ({
      id: department.DepartmentID,
      name: department.name,
      shorthand_symbol: department.shorthand_symbol,
      color_hex: department.color_hex,
    })),
    shift_list: shiftList,
    event_data_web: demoMonthShiftEvents(),
    absence_list: [
      { id: 1, name: "Krankheit", color_hex: "#C81E4B", icon_key: "sick", counts_as_work_time: true, is_active: true },
      { id: 2, name: "Weiterbildung", color_hex: "#2563EB", icon_key: "education", counts_as_work_time: true, is_active: true },
    ],
    absence_archive_list: [],
    user_holiday_type_list: [
      { id: 1, name: "Ferien", hours_per_day: 8, is_active: true, is_system_default: true, start_time: "00:00", end_time: "23:59" },
    ],
    user_holiday_type_archive_list: [],
    user_holiday_list: [
      { id: 1, user_id: 1, holiday_type_id: 1, holiday_type_name: "Ferien", start_date: `${monthKey}-10`, end_date: `${monthKey}-12`, hours_per_day_snapshot: 8, total_hours: 24, approval_required: false, planned_minutes_per_day: DEMO_DAILY_TARGET_HOURS * 60 },
      { id: 2, user_id: 6, holiday_type_id: 1, holiday_type_name: "Ferien", start_date: `${monthKey}-17`, end_date: `${monthKey}-21`, hours_per_day_snapshot: 8, total_hours: 40, approval_required: false, planned_minutes_per_day: DEMO_DAILY_TARGET_HOURS * 60 },
      { id: 3, user_id: 11, holiday_type_id: 1, holiday_type_name: "Ferien", start_date: `${monthKey}-24`, end_date: `${monthKey}-26`, hours_per_day_snapshot: 8, total_hours: 24, approval_required: false, planned_minutes_per_day: DEMO_DAILY_TARGET_HOURS * 60 },
    ],
    user_availability_list: userAvailabilityEntries,
    user_availability_default_list: userAvailabilityDefaults,
    public_holidays: [{ id: 1, date: `${monthKey}-01`, name: "Bundesfeier", local_name: "Bundesfeier", source_type: "public" }],
    day_comment_list: [
      { id: 1, date: `${monthKey}-08`, comment: "Sommerfest – erhöhte Auslastung" },
      { id: 2, date: `${monthKey}-22`, comment: "Grossanlass im Haus" },
    ],
    color_map: colorMap,
    access_level: "Super_Admin",
    company_locations: [{ id: 1, name: "Demo Standort" }],
    location: { id: 1, name: "Demo Standort" },
    display_calendar: "month",
    shift_display_mode: "shift",
    company_availability_mode: "absence",
    user_availability_mode_by_user_id: Object.fromEntries(demoUsers.map((user) => [user.UserID, "absence"])),
    working_days: 5,
    current_week_num: isoWeekNumber(),
    // Die Demo zeigt den vollen Freigabe-Ablauf inklusive Benachrichtigung.
    whatsapp_subscription: true,
  };
}

/*
  Nur die zeitabhängigen Daten eines Zeitraums — dieselbe Form wie /entries.

  Die Demo-Daten liegen alle im laufenden Monat. Ein Zeitraum daneben liefert
  darum berechtigterweise nichts; das entspricht genau dem, was der Server täte.
*/
function shiftplanEntriesResponse(config) {
  const bootstrap = shiftplanBootstrapResponse();
  const draftsOnly = ["1", "true", "yes"].includes(String(requestParam(config, "drafts_only", "")).toLowerCase());

  if (draftsOnly) {
    return {
      event_data_web: bootstrap.event_data_web.filter((event) => event.release_status !== "released"),
      user_holiday_list: [],
      public_holidays: [],
      public_holiday_dates: [],
      day_comment_list: [],
      period: null,
    };
  }

  const { start, end } = requestedRange(config, true);
  const startKey = localDateKey(start);
  const endKey = localDateKey(end);
  const overlaps = (from, to) => String(from || "").slice(0, 10) <= endKey && String(to || from || "").slice(0, 10) >= startKey;

  const publicHolidays = bootstrap.public_holidays.filter((row) => overlaps(row.date, row.date));

  return {
    event_data_web: bootstrap.event_data_web.filter((event) => overlaps(event.start_at, event.end_at)),
    user_holiday_list: bootstrap.user_holiday_list.filter((row) => overlaps(row.start_date, row.end_date)),
    public_holidays: publicHolidays,
    public_holiday_dates: publicHolidays.map((row) => String(row.date).slice(0, 10)),
    day_comment_list: bootstrap.day_comment_list.filter((row) => overlaps(row.date, row.date)),
    period: { start_date: startKey, end_date: endKey },
  };
}

function shiftplanWorkloadResponse(config) {
  const monthEvents = demoMonthShiftEvents();
  const { start, end } = requestedRange(config, true);
  const periodDays = dateRange(start, end);
  const periodStartKey = localDateKey(start);
  const periodEndKey = localDateKey(end);
  const periodEvents = monthEvents.filter((event) => {
    const eventDate = event.start_at.slice(0, 10);
    return eventDate >= periodStartKey && eventDate <= periodEndKey;
  });
  const workingDayCount = periodDays.filter(isDemoWorkingDay).length;
  const holidayRanges = {
    1: [10, 12],
    6: [17, 21],
    11: [24, 26],
  };
  const workloadByEmployeeId = Object.fromEntries(demoUsers.map((user) => {
    // Dieselbe Regel wie im Backend: Schichten am Starttag, Absenzen und
    // Ferien pro Arbeitstag, gerundet auf Minuten und dann summiert.
    const shiftMinutes = periodEvents
      .filter((event) => event.entry_type === "shift" && event.user_id === user.UserID)
      .reduce((total, event) => total + (Number(event.planned_minutes) || 0), 0);
    const absenceMinutes = periodEvents
      .filter((event) => event.entry_type === "absence" && event.user_id === user.UserID)
      .reduce((total, event) => {
        const date = new Date(`${event.start_at.slice(0, 10)}T12:00:00`);
        return total + (isDemoWorkingDay(date) ? Number(event.planned_minutes_per_day) || 0 : 0);
      }, 0);
    const targetHours = workingDayCount * DEMO_DAILY_TARGET_HOURS * user.employment_level;
    const holidayRange = holidayRanges[user.UserID];
    const vacationDays = holidayRange
      ? periodDays.filter((date) => {
        const day = date.getDate();
        return day >= holidayRange[0] && day <= holidayRange[1] && isDemoWorkingDay(date);
      }).length
      : 0;
    const holidayMinutes = vacationDays * DEMO_DAILY_TARGET_HOURS * 60;
    return [String(user.UserID), {
      user_id: user.UserID,
      planned_hours: Math.round(((shiftMinutes + absenceMinutes + holidayMinutes) / 60) * 10) / 10,
      target_hours: Math.round(targetHours * 10) / 10,
      vacation_days: vacationDays,
    }];
  }));
  return {
    workload_by_employee_id: workloadByEmployeeId,
    period_summary: {
      planned_hours: Object.values(workloadByEmployeeId).reduce((sum, row) => sum + row.planned_hours, 0),
      shift_count: periodEvents.filter((event) => event.entry_type === "shift").length,
      absence_count: periodEvents.filter((event) => event.entry_type === "absence").length,
      vacation_count: Object.values(workloadByEmployeeId).reduce((sum, row) => sum + row.vacation_days, 0),
    },
  };
}

function shiftplanDemandResponse(config) {
  const requestedStart = requestParam(config, "start_date");
  const start = requestedStart ? new Date(`${requestedStart}T12:00:00`) : startOfWeek();
  const requestedEnd = requestParam(config, "end_date");
  const end = requestedEnd ? new Date(`${requestedEnd}T12:00:00`) : new Date(start);
  if (!requestedEnd) end.setDate(start.getDate() + 13);
  const days = dateRange(start, end, 62);
  return {
    demand_list: days.flatMap((date, dayIndex) =>
      demoShifts.flatMap((shift, shiftIndex) => {
        const requiredWorkers = demoRequiredWorkers(date, shiftIndex);
        if (!requiredWorkers) return [];
        const department = demoDepartments[shiftIndex % demoDepartments.length];
        return [{
          id: (dayIndex * demoShifts.length) + shiftIndex + 1,
          date: localDateKey(date),
          required_workers: requiredWorkers,
          template_id: String(shift.ShiftID),
          shift_id: shift.ShiftID,
          department_id: department.DepartmentID,
          shift_name: shift.name,
          shift_shorthand: shift.shorthand_symbol,
          department_name: department.name,
          department_shorthand: department.shorthand_symbol,
          start_time: shift.start_time,
          end_time: shift.end_time,
          color_hex: department.color_hex,
        }];
      })
    ),
  };
}

function availabilityExportPreviewResponse(config) {
  const { start, end } = requestedRange(config, true);
  const columns = [
    ["employee", "Mitarbeitende"], ["email", "E-Mail"], ["date", "Datum"],
    ["weekday", "Wochentag"], ["calendar_week", "KW"], ["status", "Status"],
    ["source", "Quelle"], ["mode", "Modus"], ["full_day", "Ganzer Tag"],
    ["start_time", "Von"], ["end_time", "Bis"], ["approval_status", "Freigabe"],
    ["comment", "Kommentar"],
  ].map(([id, label]) => ({ id, label }));
  const rows = demoAvailabilityRows(start, end).flatMap((user) =>
    Object.entries(user.per_day).map(([date, day]) => ({
      employee: user.name,
      email: user.email,
      date,
      weekday: new Intl.DateTimeFormat("de-CH", { weekday: "long" }).format(new Date(`${date}T12:00:00`)),
      calendar_week: isoWeekNumber(new Date(`${date}T12:00:00`)),
      status: day.status === "availability" ? "Verfügbar" : day.status === "holiday" ? "Ferien" : "Abwesend",
      source: day.is_default ? "Standard" : day.isHoliday ? "Ferien" : "Eintrag",
      mode: "Abwesenheit",
      full_day: day.is_full_day ? "Ja" : "Nein",
      start_time: day.is_full_day ? "" : day.availability_time.slice(0, 5),
      end_time: day.is_full_day ? "" : day.availability_time.slice(-5),
      approval_status: day.is_pending ? "Ausstehend" : "Freigegeben",
      comment: day.has_comment ? "Demo-Eintrag" : "",
    }))
  );
  return {
    columns,
    default_column_ids: columns.map((column) => column.id),
    tab_modes: [{ id: "none", label: "Keine Aufteilung" }, { id: "user", label: "Mitarbeitende" }, { id: "department", label: "Abteilungen" }],
    default_tab_mode: "none",
    filter_groups: [{ id: "statuses", label: "Einträge", helperText: "", options: [
      { id: "availability", label: "Verfügbar" }, { id: "absence", label: "Abwesend" }, { id: "holiday", label: "Ferien" },
    ] }],
    default_filters: { statuses: [] },
    period_label: `${localDateKey(start)} – ${localDateKey(end)}`,
    preview_rows: rows.slice(0, 8),
    preview_sections: [{ id: "none", title: "Verfügbarkeit", columns, rows: rows.slice(0, 8) }],
    scope_label: "Demo Standort · 15 Mitarbeitende",
    file_name: `Verfügbarkeit_${localDateKey(start).slice(0, 7)}.xlsx`,
  };
}

function shiftplanExportPreviewResponse(config) {
  const columns = [
    ["employee", "Mitarbeitende"], ["date", "Datum"], ["type", "Typ"],
    ["department", "Abteilung"], ["shift", "Schicht"], ["timeframe", "Zeitraum"],
    ["planned_hours", "Geplante Stunden"], ["status", "Status"],
  ].map(([id, label]) => ({ id, label }));
  const usersById = Object.fromEntries(demoUsers.map((user) => [user.UserID, user]));
  const rows = demoMonthShiftEvents().map((event) => {
    const user = usersById[event.user_id];
    const startTime = event.start_at.slice(11, 16);
    const endTime = event.end_at.slice(11, 16);
    return {
      employee: `${user.first_name} ${user.last_name}`,
      date: event.start_at.slice(0, 10),
      type: event.entry_type === "absence" ? "Abwesenheit" : "Schicht",
      department: event.department_name,
      shift: event.title,
      timeframe: event.is_all_day ? "Ganztag" : `${startTime}–${endTime}`,
      planned_hours: event.is_all_day ? "08:00" : "07:30",
      status: event.release_status === "released" ? "Freigegeben" : "Entwurf",
    };
  });
  const month = requestParam(config, "month", localDateKey(new Date()).slice(0, 7));
  return {
    period_label: month,
    scope_label: "Demo Standort",
    columns,
    default_column_ids: columns.map((column) => column.id),
    tab_modes: [{ id: "none", label: "Keine Aufteilung" }, { id: "user", label: "Mitarbeitende" }, { id: "department", label: "Abteilungen" }],
    default_tab_mode: "none",
    filter_groups: [
      { id: "employees", label: "Mitarbeitende", options: demoUsers.map((user) => ({ id: String(user.UserID), label: `${user.first_name} ${user.last_name}` })) },
      { id: "entry_types", label: "Typ", options: [{ id: "shift", label: "Schicht" }, { id: "absence", label: "Abwesenheit" }, { id: "holiday", label: "Ferien" }] },
    ],
    default_filters: { employees: [], entry_types: [] },
    preview_rows: rows.slice(0, 8),
    preview_sections: [{ id: "none", title: "Schichtplan", columns, rows: rows.slice(0, 8) }],
  };
}

const responders = [
  ["/api/web/dashboard", dashboardResponse],
  ["/api/web/approval/count", () => ({ count: 8 })],
  ["/api/web/approval", approvalResponse],
  ["/api/web/team/users", usersResponse],
  ["/api/web/feature-special/punch_clock", () => ({ enabled: false })],
  ["/api/web/feature-special/document_upload", () => ({ enabled: false })],
  ["/api/web/current_user", currentUserResponse],
  ["/api/web/feature-web", () => []],
  ["/api/web/billing/access/page.shiftplan", () => ({ has_access: true, subscription_status: "demo" })],
  ["/api/web/punch-clock", punchClockResponse],
  ["/api/web/availability/context", availabilityContextResponse],
  ["/api/web/availability/calendar", availabilityCalendarResponse],
  ["/api/web/availability/overview", availabilityOverviewResponse],
  ["/api/web/availability/export/preview", availabilityExportPreviewResponse],
  ["/api/web/shiftplan-reporting/export/preview", shiftplanExportPreviewResponse],
  ["/api/web/demand", currentDemandResponse],
  ["/api/app/notifications", () => []],
  ["/api/shiftplan/bootstrap", shiftplanBootstrapResponse],
  ["/api/shiftplan/entries", shiftplanEntriesResponse],
  ["/api/shiftplan/workload", shiftplanWorkloadResponse],
  ["/api/shiftplan/demand", shiftplanDemandResponse],
];

export function getDemoPayload(config) {
  if (!isDemoMode() || !READ_METHODS.has((config.method || "get").toLowerCase())) {
    return null;
  }

  const url = new URL(config.url, window.location.origin);
  const match = responders.find(([path]) => url.pathname === path);
  return match ? match[1](config) : null;
}

export function getDemoResponse(config) {
  const payload = getDemoPayload(config);
  if (payload === null) return null;

  return {
    data: payload,
    status: 200,
    statusText: "OK",
    headers: {},
    config,
    request: null,
  };
}

export function isDemoMutation(config) {
  return isDemoMode() && !READ_METHODS.has((config.method || "get").toLowerCase());
}

export { demoCompany };
